import { getUserPaymentMethodsAdmin } from '@/api/payments';
import { updateFranchise } from '@/api/admin';
import { FranchiseStandingStatus, PaymentMethod, User } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Typography,
  Collapse,
  Flex,
  Switch,
  Select,
  message,
  Tooltip,
} from 'antd';
import { CollapseProps } from 'antd/lib';
import FranchiseDetailsAdminSection from './FranchiseDetailsAdminSection';
import PendingUpgradeRequestLine from './PendingUpgradeRequestLine';
import GalleryAdminSection from './GalleryAdminSection';
import FranchisorContactAdminSection from './FranchisorContactAdmiSection';
import FranchisorInvestmentProfile from './FranchisorInvestmentProfile';
import FranchisorTrainingAdminSection from './FranchisorTrainingAdminSection';
import FranchisorCommissionsAdminSections from './FranchisorCommissionsAdminSections';
import FranchisePaymentAndSubmissionDetails from './FranchisePaymentAndSubscriptionAdminSection';
import { formatDate } from '@/helpers/dateFormat';
import { SvgIcon } from '@/components/icon';
import { CheckOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { CSSProperties, useState } from 'react';
import RatingDetailsModal from './RatingDetailsModal';
import LegalDocumentsSection from './LegalDocumentsSection';
import UniversalReferralTermsHistory from './UniversalReferralTermsHistory';
import { useFranchiseLegalAcceptances } from '@/hooks/useLegalDocuments';
// import AdditionalCommentsModal from './AdditionalCommentsModal';

interface Props {
  franchise: any;
}

const STANDING_OPTIONS: Array<{
  value: FranchiseStandingStatus;
  label: string;
  color: string;
}> = [
  {
    value: FranchiseStandingStatus.GOOD_STANDING,
    label: 'Good Standing',
    color: '#349D63',
  },
  {
    value: FranchiseStandingStatus.IN_REVIEW,
    label: 'In Review',
    color: '#C9942C',
  },
  {
    value: FranchiseStandingStatus.OUTSTANDING_DISPUTE,
    label: 'Outstanding Dispute',
    color: '#D74846',
  },
];

// `colored` drives the closed pill (dot + text both tinted to match the
// status); the open dropdown reuses this with black labels + colored dots.
const renderStandingOption = (
  option: (typeof STANDING_OPTIONS)[number],
  colored = false,
) => (
  <Flex align="center" gap={8}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: option.color,
        display: 'inline-block',
        flex: 'none',
      }}
    />
    <span style={{ color: colored ? option.color : '#2b333c' }}>
      {option.label}
    </span>
  </Flex>
);

export default function FranchisorDetailed({ franchise }: Props): JSX.Element {
  const [ratingOpen, setRatingOpen] = useState(false);
  // const [commentsOpen, setCommentsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: paymentMethod } = useQuery<PaymentMethod | null, Error>({
    queryKey: ['payment-methods', franchise],
    queryFn: () => {
      if (!franchise.primaryPaymentMethod) return null;
      return getUserPaymentMethodsAdmin(franchise.primaryPaymentMethod || '');
    },
  });

  // TCX-669: legal acceptance audit trail for this franchise's members.
  const {
    data: legalHistory,
    isLoading: legalLoading,
    isError: legalError,
  } = useFranchiseLegalAcceptances(franchise?.id);

  const standingMutation = useMutation({
    mutationFn: (value: FranchiseStandingStatus) =>
      updateFranchise({
        id: franchise.id,
        data: { standing: value },
      }),

    onMutate: async (value) => {
      await queryClient.cancelQueries({
        queryKey: ['franchisor', franchise.id],
      });

      const prev = queryClient.getQueryData(['franchisor', franchise.id]);

      queryClient.setQueryData(
        ['franchisor', franchise.id],
        (old: Record<string, unknown> | undefined) =>
          old ? { ...old, standing: value } : old,
      );

      return { prev };
    },

    onError: (_error, _value, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['franchisor', franchise.id], ctx.prev);
      }
      message.error('Failed to update Standing Status');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise.id],
      });
    },

    onSuccess: (_data, value) => {
      const label =
        STANDING_OPTIONS.find((o) => o.value === value)?.label ?? value;
      message.success(`Standing Status updated to ${label}`);
    },
  });

  // TCX-815: "Offer TCX Connect" only applies to a franchisor who can still
  // pick a plan — the backend rejects enabling it during a live subscription
  // (and the one-shot reset has already cleared it), so disable the switch
  // rather than let the flip bounce off the guard.
  const isSubscriptionActive = ['active', 'trialing'].includes(
    franchise?.stripeSubscriptionStatus ?? '',
  );

  const currentStanding: FranchiseStandingStatus =
    franchise?.standing ?? FranchiseStandingStatus.GOOD_STANDING;
  const currentStandingOption =
    STANDING_OPTIONS.find((o) => o.value === currentStanding) ??
    STANDING_OPTIONS[0];

  const independentConsultantFriendlyMutation = useMutation({
    mutationFn: (checked: boolean) =>
      updateFranchise({
        id: franchise.id,
        data: { isIndependentConsultantFriendly: checked },
      }),

    onMutate: async (checked) => {
      await queryClient.cancelQueries({
        queryKey: ['franchisor', franchise.id],
      });

      const prev = queryClient.getQueryData(['franchisor', franchise.id]);

      queryClient.setQueryData(
        ['franchisor', franchise.id],
        (old: Record<string, unknown> | undefined) =>
          old
            ? { ...old, isIndependentConsultantFriendly: checked }
            : old,
      );

      return { prev };
    },

    onError: (_error, _checked, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['franchisor', franchise.id], ctx.prev);
      }
      message.error('Failed to update Independent Consultant Friendly status');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise.id],
      });
    },

    onSuccess: (_data, checked) => {
      message.success(
        `Independent Consultant Friendly ${checked ? 'enabled' : 'disabled'}`,
      );
    },
  });

  // TCX-815: admin "Offer TCX Connect" switch. When on, the hidden $99 Connect
  // plan is surfaced on this franchisor's own subscription page. The backend
  // rejects enabling it for a franchisor with a live subscription and clears it
  // (one-shot) once any plan activates, so the switch is disabled for active
  // subscribers (see isSubscriptionActive at the render site).
  const connectOfferedMutation = useMutation({
    mutationFn: (checked: boolean) =>
      updateFranchise({
        id: franchise.id,
        data: { connectOffered: checked },
      }),

    onMutate: async (checked) => {
      await queryClient.cancelQueries({
        queryKey: ['franchisor', franchise.id],
      });

      const prev = queryClient.getQueryData(['franchisor', franchise.id]);

      queryClient.setQueryData(
        ['franchisor', franchise.id],
        (old: Record<string, unknown> | undefined) =>
          old ? { ...old, connectOffered: checked } : old,
      );

      return { prev };
    },

    onError: (error: any, _checked, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['franchisor', franchise.id], ctx.prev);
      }
      // Surface the backend guard's reason (e.g. active subscription) when
      // available, so a rejected flip isn't a mystery.
      message.error(
        error?.response?.data?.message ||
          'Failed to update Offer TCX Connect status',
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise.id],
      });
    },

    onSuccess: (_data, checked) => {
      message.success(`Offer TCX Connect ${checked ? 'enabled' : 'disabled'}`);
    },
  });

  const tcxMemberMutation = useMutation({
    mutationFn: (checked: boolean) =>
      updateFranchise({
        id: franchise.id,
        data: { isTcxMember: checked },
      }),

    onMutate: async (checked) => {
      await queryClient.cancelQueries({
        queryKey: ['franchisor', franchise.id],
      });

      const prev = queryClient.getQueryData(['franchisor', franchise.id]);

      queryClient.setQueryData(
        ['franchisor', franchise.id],
        (old: Record<string, unknown> | undefined) =>
          old ? { ...old, isTcxMember: checked } : old,
      );

      return { prev };
    },

    onError: (_error, _checked, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['franchisor', franchise.id], ctx.prev);
      }
      message.error('Failed to update TCX Member status');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise.id],
      });
    },

    onSuccess: (_data, checked) => {
      message.success(`TCX Member ${checked ? 'enabled' : 'disabled'}`);
    },
  });

  // TCX-740: per-franchisor Universal Referral Terms feature gate.
  const urtEnabledMutation = useMutation({
    mutationFn: (checked: boolean) =>
      updateFranchise({
        id: franchise.id,
        data: { isUniversalReferralTermsEnabled: checked },
      }),

    onMutate: async (checked) => {
      await queryClient.cancelQueries({
        queryKey: ['franchisor', franchise.id],
      });

      const prev = queryClient.getQueryData(['franchisor', franchise.id]);

      queryClient.setQueryData(
        ['franchisor', franchise.id],
        (old: Record<string, unknown> | undefined) =>
          old ? { ...old, isUniversalReferralTermsEnabled: checked } : old,
      );

      return { prev };
    },

    onError: (_error, _checked, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['franchisor', franchise.id], ctx.prev);
      }
      message.error('Failed to update Universal Referral Terms status');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise.id],
      });
    },

    onSuccess: (_data, checked) => {
      message.success(
        `Universal Referral Terms ${checked ? 'enabled' : 'disabled'}`,
      );
    },
  });

  const items: CollapseProps['items'] = [
    // {
    //   key: '1',
    //   label: 'Other Users',
    //   style: {
    //     borderBottom: '1px solid #f0f0f2',
    //   },
    //   children: (
    //     <FranchiseOtherUsersAdminSection
    //       users={franchise?.users?.filter(
    //         (user: Partial<User>) => user.id !== franchise.activatedBy,
    //       )}
    //     />
    //   ),
    // },
    {
      key: '2',
      label: 'Payment & Subscription',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: (
        <FranchisePaymentAndSubmissionDetails
          paymentMethod={paymentMethod}
          subscriptionStatus={franchise?.stripeSubscriptionStatus}
        />
      ),
    },
    {
      key: '3',
      label: 'Franchise Details',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: <FranchiseDetailsAdminSection franchisor={franchise} />,
    },
    {
      key: '4',
      label: 'Gallery',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: (
        <GalleryAdminSection
          photos={franchise?.photos?.filter(Boolean) || []}
        />
      ),
    },
    {
      key: '5',
      label: 'Contact',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: <FranchisorContactAdminSection franchisor={franchise} />,
    },
    {
      key: '6',
      label: 'Investment Profile',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: <FranchisorInvestmentProfile franchisor={franchise} />,
    },
    {
      key: '7',
      label: 'Training and Support',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: <FranchisorTrainingAdminSection franchisor={franchise} />,
    },
    {
      key: '8',
      label: 'Commissions',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: (
        <FranchisorCommissionsAdminSections
          commissions={franchise?.commissions || undefined}
        />
      ),
    },
    {
      key: '9',
      label: 'Commissions International',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: (
        <FranchisorCommissionsAdminSections
          commissions={franchise?.internationalCommissions || undefined}
        />
      ),
    },
    {
      // TCX-739: audit trail of every published Universal Referral Terms
      // version for this franchisor (descending, CURRENT = live on profile).
      key: '10',
      label: 'Universal Referral Terms',
      style: {
        borderBottom: '1px solid #f0f0f2',
      },
      children: <UniversalReferralTermsHistory franchiseId={franchise?.id} />,
    },
  ];

  const activatedBy = franchise?.users.find(
    (user: Partial<User>) => user.id === franchise.activatedBy,
  );
  const activatedByName = `${activatedBy?.firstName || ''} ${activatedBy?.lastName || ''}`.trim();

  return (
    <div className={'detailed-wrapper'}>
      <div className={'detailed-content'}>
        <Typography.Title level={3} style={{ margin: '0 0 24px' }}>
          General Info
        </Typography.Title>
        <div className={'details-sections'}>
          <div className={'details-section'}>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Activated By
              </Typography.Text>
              <Typography.Text className={'general-info-value'}>
                {activatedBy?.firstName} {activatedBy?.lastName}
              </Typography.Text>
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Email
              </Typography.Text>
              <Typography.Text className={'general-info-value'}>
                {activatedBy?.email}
              </Typography.Text>
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Status
              </Typography.Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: activatedBy?.status
                      ? '#1C8271'
                      : '#F68B8B',
                  }}
                />
                <Typography.Text className={'general-info-value'}>
                  {activatedBy?.status ? 'Active' : 'Inactive'}
                </Typography.Text>
              </div>
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Rate
              </Typography.Text>

              <Flex align="center" gap={6}>
                <SvgIcon type={'goldStar'} width={16} height={16} />

                <Typography.Text className={'general-info-value'}>
                  {(Math.floor((franchise?.nps ?? 0) * 10) / 10).toFixed(1)}
                </Typography.Text>

                <Typography.Text
                  className="rating-link"
                  onClick={() => setRatingOpen(true)}
                >
                  Details
                </Typography.Text>
              </Flex>
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Last Update
              </Typography.Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text className={'general-info-value'}>
                  {(franchise?.franchiseUpdatedTimeStamp &&
                    formatDate(
                      new Date(franchise.franchiseUpdatedTimeStamp),
                    )) ||
                    (franchise?.createdAt &&
                      formatDate(new Date(franchise.createdAt)))}
                </Typography.Text>
              </div>
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                TCX Member
              </Typography.Text>
              <Switch
                checked={!!franchise?.isTcxMember}
                onChange={(checked) => tcxMemberMutation.mutate(checked)}
                style={{
                  backgroundColor: franchise?.isTcxMember
                    ? '#1C8271'
                    : '#D1CECE',
                }}
              />
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Independent Consultant Friendly
              </Typography.Text>
              <Switch
                checked={!!franchise?.isIndependentConsultantFriendly}
                onChange={(checked) =>
                  independentConsultantFriendlyMutation.mutate(checked)
                }
                style={{
                  backgroundColor: franchise?.isIndependentConsultantFriendly
                    ? '#2A4BC2'
                    : '#D1CECE',
                }}
              />
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Offer TCX Connect
              </Typography.Text>
              <Tooltip
                title={
                  isSubscriptionActive
                    ? 'Not available while this franchisor has an active subscription.'
                    : undefined
                }
              >
                <Switch
                  checked={!!franchise?.connectOffered}
                  disabled={isSubscriptionActive}
                  onChange={(checked) => connectOfferedMutation.mutate(checked)}
                  style={{
                    backgroundColor: franchise?.connectOffered
                      ? '#1C8271'
                      : '#D1CECE',
                  }}
                />
              </Tooltip>
            </div>
            {/* TCX-843: renders only while an upgrade request is pending. */}
            <PendingUpgradeRequestLine franchise={franchise} />

            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Enable Universal Referral Terms
              </Typography.Text>
              <Switch
                checked={!!franchise?.isUniversalReferralTermsEnabled}
                onChange={(checked) => urtEnabledMutation.mutate(checked)}
                style={{
                  backgroundColor: franchise?.isUniversalReferralTermsEnabled
                    ? '#1C8271'
                    : '#D1CECE',
                }}
              />
            </div>
            <div className={'details-section-line'}>
              <Typography.Text className={'general-info-text'}>
                Standing Status
              </Typography.Text>
              <div
                className={'standing-pill'}
                style={
                  {
                    borderColor: `${currentStandingOption.color}59`,
                    color: currentStandingOption.color,
                    '--standing-color': currentStandingOption.color,
                    height: 25,
                  } as CSSProperties
                }
              >
                <Select<FranchiseStandingStatus>
                  value={currentStanding}
                  onChange={(value) => standingMutation.mutate(value)}
                  loading={standingMutation.isPending}
                  disabled={standingMutation.isPending}
                  variant="borderless"
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  className={'standing-select'}
                  popupClassName={'standing-dropdown'}
                  menuItemSelectedIcon={<CheckOutlined />}
                  options={STANDING_OPTIONS.map((o) => ({
                    value: o.value,
                    label: renderStandingOption(o),
                  }))}
                  labelRender={() =>
                    renderStandingOption(currentStandingOption, true)
                  }
                />
              </div>
            </div>
          </div>
          <LegalDocumentsSection
            history={legalHistory}
            loading={legalLoading}
            isError={legalError}
            showUserColumn
          />
          <Collapse
            items={items}
            size={'large'}
            style={{
              backgroundColor: 'transparent',
            }}
            bordered={false}
          />
        </div>
      </div>
      {/* <div className="detailed-footer">
        <Link to={'#'} className="detailed-footer-link-wrapper">
          <div className="icon-link">
            <SvgIcon type="mail" width={14} height={14} />
          </div>
          <Typography.Text className="general-info-text">Mail</Typography.Text>
        </Link>
        <Link to={'#'} className="detailed-footer-link-wrapper">
          <div className="icon-link">
            <SvgIcon type="linkedin" width={14} height={14} />
          </div>

          <Typography.Text className="general-info-text">
            LinedIn
          </Typography.Text>
        </Link>
        <Link to={'#'} className="detailed-footer-link-wrapper">
          <div className="icon-link">
            <SvgIcon type="instagram" width={14} height={14} />
          </div>
          <Typography.Text className="general-info-text">
            Instagram
          </Typography.Text>
        </Link>
        <Link to={'#'} className="detailed-footer-link-wrapper">
          <div className="icon-link">
            <SvgIcon type="facebook" width={14} height={14} />
          </div>
          <Typography.Text className="general-info-text">
            Facebook
          </Typography.Text>
        </Link>
      </div> */}
      <RatingDetailsModal
        open={ratingOpen}
        onClose={() => setRatingOpen(false)}
        franchisorId={franchise?.id}
        name={activatedByName || activatedBy?.email || franchise?.company || 'Franchisor'}
      />
      {/* <AdditionalCommentsModal
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        name={activatedByName || activatedBy?.email || franchise?.company || 'Franchisor'}
        franchisorId={franchise?.id}
      /> */}
    </div>
  );
}
